import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Loader,
  Save,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Upload,
  FileText,
  LockKeyhole,
  Wand2,
  PenLine,
  X,
  UserCircle,
  ChevronRight,
  FileSearch,
} from 'lucide-react';
import { templateApi, TemplateSection, ClassifiedSection, SectionType, CredentialSource } from '../services/templateApi';

const CATEGORIES = [
  'Neurology',
  'Orthopaedics',
  'Psychiatry',
  'Pain Medicine',
  'General Surgery',
  'Radiology',
  'Cardiology',
  'Obstetrics & Gynaecology',
  'Respiratory Medicine',
  'ENT',
  'Medical-Legal',
  'Legal',
  'Summary',
  'Clinical',
];

const SECTION_TYPE_CONFIG: Record<SectionType, { label: string; icon: React.ReactNode; bg: string; text: string; description: string }> = {
  static:      { label: 'Static',      icon: <LockKeyhole className="h-3.5 w-3.5" />, bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', description: 'Kept verbatim from the original document' },
  variable:    { label: 'AI-Generated', icon: <Sparkles className="h-3.5 w-3.5" />,    bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', description: 'Fresh AI-generated content from case records' },
  placeholder: { label: 'Manual',       icon: <PenLine className="h-3.5 w-3.5" />,     bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', description: 'Blanked out for the author to fill in' },
};

function createEmptySection(): TemplateSection {
  return {
    id: crypto.randomUUID(),
    title: '',
    placeholder: '',
    required: false,
    aiGenerated: false,
  };
}

export const TemplateFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== 'new';
  const startWithUpload = searchParams.get('upload') === 'true';

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const isPublic = false; // Public templates disabled — all templates are private
  const [sections, setSections] = useState<TemplateSection[]>([createEmptySection()]);

  // DOCX upload state
  const [docxFile, setDocxFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [classifiedSections, setClassifiedSections] = useState<ClassifiedSection[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [expandedPreviews, setExpandedPreviews] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch existing template for edit mode
  const { data: existingTemplate, isLoading } = useQuery(
    ['template', id],
    () => templateApi.getTemplate(id!),
    { enabled: isEdit }
  );

  // Populate form state when template data arrives (more reliable than onSuccess across navigations)
  useEffect(() => {
    if (!existingTemplate) return;
    setName(existingTemplate.name);
    setCategory(existingTemplate.category || CATEGORIES[0]);
    setDescription(existingTemplate.description || '');
    const parsed = templateApi.parseSections(existingTemplate.sections);
    setSections(parsed.length > 0 ? parsed : [createEmptySection()]);
  }, [existingTemplate]);

  // --- DOCX upload ---

  const handleDocxUpload = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setErrors(['Only DOCX files are supported.']);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors(['File too large. Maximum size is 10 MB.']);
      return;
    }

    setDocxFile(file);
    setErrors([]);
    setIsExtracting(true);

    try {
      const classified = await templateApi.extractSectionsFromDocx(file);
      // Stamp originalSectionType so we can enforce opinion-section restrictions
      setClassifiedSections(classified.map(s => ({ ...s, originalSectionType: s.sectionType })));

      // Don't auto-populate name from filename — it often contains patient-specific data
    } catch (err: any) {
      setErrors([err?.message || 'Failed to analyse the document. Please try again.']);
      setDocxFile(null);
      setClassifiedSections(null);
    } finally {
      setIsExtracting(false);
    }
  }, [name]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleDocxUpload(file);
  }, [handleDocxUpload]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleDocxUpload(file);
  }, [handleDocxUpload]);

  const clearDocxUpload = useCallback(() => {
    setDocxFile(null);
    setClassifiedSections(null);
    setSections([createEmptySection()]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const updateClassifiedSectionType = useCallback((index: number, newType: SectionType) => {
    setClassifiedSections(prev => {
      if (!prev) return prev;
      return prev.map((s, i) => i === index ? { ...s, sectionType: newType } : s);
    });
  }, []);

  // --- Section management (manual mode) ---

  const addSection = () => {
    setSections((prev) => [...prev, createEmptySection()]);
  };

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSection = (index: number, field: keyof TemplateSection, value: string | boolean) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // --- Validation & submit ---

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!name.trim()) errs.push('Template name is required.');

    if (classifiedSections) {
      if (classifiedSections.length === 0) errs.push('At least one section is required.');
    } else {
      if (sections.length === 0) errs.push('At least one section is required.');
      sections.forEach((s, i) => {
        if (!s.title.trim()) errs.push(`Section ${i + 1} needs a title.`);
      });
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setIsSubmitting(true);

    try {
      if (classifiedSections && docxFile) {
        // DOCX-based template creation
        const sectionDefs = classifiedSections.map((s, i) => {
          // Credential sections set to 'profile' become variable (AI-generated from expert profile)
          const useProfile = s.isCredentialSection && s.credentialSource === 'profile';
          const effectiveType = useProfile ? 'variable' : s.sectionType;

          return {
            id: s.id,
            title: s.title,
            originalTitle: s.originalTitle && s.originalTitle !== s.title ? s.originalTitle : undefined,
            sectionType: effectiveType,
            credentialSource: s.isCredentialSection ? (s.credentialSource || 'document') : undefined,
            placeholder: useProfile
              ? 'Generate this section from the expert witness professional profile. Include qualifications, professional memberships, relevant experience, and registration details as available in the profile.'
              : (s.guidance || (s.sectionType === 'placeholder' ? 'To be completed by the author' : '')),
            required: effectiveType === 'variable',
            aiGenerated: effectiveType === 'variable',
            headingLevel: s.headingLevel,
            depth: s.depth,
            order: i,
          };
        });

        const created = await templateApi.createFromDocx(
          docxFile,
          name.trim(),
          category,
          JSON.stringify(sectionDefs),
          description.trim() || undefined,
        );
        navigate(`/templates/${created.id}`);
      } else if (isEdit) {
        const orderedSections = sections.map((s, i) => ({ ...s, order: i }));
        await templateApi.updateTemplate(id!, {
          id: id!,
          name: name.trim(),
          category,
          description: description.trim() || undefined,
          isPublic,
          sections: templateApi.stringifySections(orderedSections),
        });
        navigate(`/templates/${id}`);
      } else {
        const orderedSections = sections.map((s, i) => ({ ...s, order: i }));
        const created = await templateApi.createTemplate({
          name: name.trim(),
          category,
          description: description.trim() || undefined,
          isPublic,
          sections: templateApi.stringifySections(orderedSections),
        });
        navigate(`/templates/${created.id}`);
      }
    } catch (err: any) {
      setErrors([err?.message || 'Failed to save template. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Loading state ---

  if (isEdit && isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading template...</span>
        </div>
      </div>
    );
  }

  const isDocxMode = !!classifiedSections;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate(isEdit ? `/templates/${id}` : '/templates')}
        className="flex items-center gap-2 text-medical-600 dark:text-medical-400 hover:text-medical-900 dark:hover:text-medical-200 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        {isEdit ? 'Back to Template' : 'Back to Templates'}
      </button>

      <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100 mb-6">
        {isEdit ? 'Edit Template' : isDocxMode ? 'Create Template from Document' : 'Create Template'}
      </h1>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* DOCX Upload Zone — shown when creating (not editing) and no DOCX yet */}
      {!isEdit && !isDocxMode && (
        <div
          className="card mb-6 border-2 border-dashed border-medical-300 dark:border-medical-600 hover:border-primary-400 dark:hover:border-primary-500 transition-colors cursor-pointer"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            onChange={handleFileChange}
            className="hidden"
          />
          {isExtracting ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <Loader className="h-8 w-8 animate-spin text-primary-600" />
              <p className="text-sm text-medical-600 dark:text-medical-400">Analysing your document...</p>
              <p className="text-xs text-medical-400 dark:text-medical-500">Extracting sections and classifying content</p>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 gap-3">
              <Upload className="h-10 w-10 text-medical-300 dark:text-medical-600" />
              <div className="text-center">
                <p className="text-sm font-medium text-medical-700 dark:text-medical-300">
                  Upload a Word document to use as a template
                </p>
                <p className="text-xs text-medical-400 dark:text-medical-500 mt-1">
                  Drag and drop a .docx file, or click to browse. Formatting, logos, and static content will be preserved on export.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info card */}
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Details</h2>

          {/* DOCX file indicator */}
          {docxFile && (
            <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
              <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primary-700 dark:text-primary-300 truncate">{docxFile.name}</p>
                <p className="text-xs text-primary-500 dark:text-primary-400">
                  Document formatting will be preserved on export
                </p>
              </div>
              <button
                type="button"
                onClick={clearDocxUpload}
                className="p-1 text-primary-400 hover:text-primary-600 dark:hover:text-primary-300"
                title="Remove document"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field w-full"
              placeholder="e.g. Neurology Condition & Prognosis Report"
              disabled={isSubmitting}
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">
              Category
            </label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-field w-full"
              disabled={isSubmitting}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-field w-full"
              rows={3}
              placeholder="Brief description of when to use this template"
              disabled={isSubmitting}
            />
          </div>

        </div>

        {/* Classified sections from DOCX */}
        {isDocxMode && classifiedSections && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">
                  Extracted Sections <span className="text-sm font-normal text-medical-500">({classifiedSections.length})</span>
                </h2>
                <p className="text-xs text-medical-500 dark:text-medical-400 mt-1">
                  Each section has been automatically classified. You can change the type before saving.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {classifiedSections.map((section, index) => {
                const config = SECTION_TYPE_CONFIG[section.sectionType];
                const isSubSection = (section.depth ?? 1) > 1;
                const isPreviewExpanded = expandedPreviews.has(index);
                // Find parent heading: walk backwards to find the nearest depth-1 section
                let parentTitle: string | null = null;
                if (isSubSection) {
                  for (let j = index - 1; j >= 0; j--) {
                    if ((classifiedSections[j].depth ?? 1) < (section.depth ?? 1)) {
                      parentTitle = classifiedSections[j].title;
                      break;
                    }
                  }
                }
                return (
                  <div
                    key={section.id}
                    className={`border rounded-lg overflow-hidden transition-colors ${
                      section.sectionType === 'variable'
                        ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50/30 dark:bg-amber-950/10'
                        : section.sectionType === 'placeholder'
                          ? 'border-cyan-200 dark:border-cyan-800/50 bg-cyan-50/30 dark:bg-cyan-950/10'
                          : 'border-medical-200 dark:border-medical-600'
                    } ${isSubSection ? 'ml-8' : ''}`}
                  >
                    {/* Section header bar */}
                    <div className={`px-4 py-3 ${isSubSection ? 'border-l-2 border-l-primary-300 dark:border-l-primary-700' : ''}`}>
                      <div className="flex items-start gap-3">
                        {/* Order number */}
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold text-xs flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>

                        {/* Title + metadata */}
                        <div className="flex-1 min-w-0">
                          {parentTitle && (
                            <p className="text-[10px] text-primary-500 dark:text-primary-400 font-medium mb-0.5">
                              ↳ under {parentTitle}
                            </p>
                          )}

                          <h3 className={`font-semibold text-medical-900 dark:text-medical-100 ${isSubSection ? 'text-sm' : 'text-base'}`}>
                            {section.title}
                          </h3>

                          {section.originalTitle && section.originalTitle !== section.title && (
                            <p className="text-[11px] text-medical-400 dark:text-medical-500 mt-0.5">
                              Original: &ldquo;{section.originalTitle}&rdquo;
                            </p>
                          )}

                          {section.classificationReason && (
                            <p className="text-xs text-medical-500 dark:text-medical-400 mt-1 leading-relaxed">
                              {section.classificationReason}
                            </p>
                          )}
                        </div>

                        {/* Section type toggle */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          {/* Medical opinion sections cannot be AI-generated */}
                          {(() => {
                            const isOpinionSection = section.originalSectionType === 'placeholder';
                            return (
                          <div className="inline-flex rounded-lg border border-medical-200 dark:border-medical-600 overflow-hidden">
                            {(['static', 'variable', 'placeholder'] as const).map(type => {
                              const tc = SECTION_TYPE_CONFIG[type];
                              const isActive = section.sectionType === type;
                              const isBlocked = type === 'variable' && isOpinionSection;
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => !isBlocked && updateClassifiedSectionType(index, type)}
                                  disabled={isSubmitting || isBlocked}
                                  title={isBlocked
                                    ? 'AI generation is not permitted for sections that may contain medical opinion. This section must be completed by the expert.'
                                    : tc.description}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-medical-200 dark:border-medical-600 ${
                                    isBlocked
                                      ? 'bg-medical-50 dark:bg-medical-900 text-medical-300 dark:text-medical-600 cursor-not-allowed line-through decoration-medical-300 dark:decoration-medical-600'
                                      : isActive
                                        ? `${tc.bg} ${tc.text}`
                                        : 'bg-white dark:bg-medical-800 text-medical-400 dark:text-medical-500 hover:bg-medical-50 dark:hover:bg-medical-700'
                                  }`}
                                >
                                  {tc.icon}
                                  <span className="hidden sm:inline">{tc.label}</span>
                                </button>
                              );
                            })}
                          </div>
                            );
                          })()}

                          {/* Credential source toggle — for credential sections */}
                          {section.isCredentialSection && section.sectionType === 'static' && (
                            <div className="inline-flex rounded-lg border border-medical-200 dark:border-medical-600 overflow-hidden">
                              {([
                                { value: 'document' as const, label: 'From document', icon: <FileText className="h-3 w-3" /> },
                                { value: 'profile' as const, label: 'From profile', icon: <UserCircle className="h-3 w-3" /> },
                              ]).map(opt => {
                                const isActive = (section.credentialSource || 'document') === opt.value;
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      setClassifiedSections(prev => prev?.map((s, i) =>
                                        i === index ? { ...s, credentialSource: opt.value } : s
                                      ) ?? null);
                                    }}
                                    disabled={isSubmitting}
                                    title={opt.value === 'profile'
                                      ? 'Generate from expert professional profile'
                                      : 'Keep content from the uploaded document'}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-medical-200 dark:border-medical-600 ${
                                      isActive
                                        ? opt.value === 'profile'
                                          ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                        : 'bg-white dark:bg-medical-800 text-medical-400 dark:text-medical-500 hover:bg-medical-50 dark:hover:bg-medical-700'
                                    }`}
                                  >
                                    {opt.icon}
                                    <span className="hidden sm:inline">{opt.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Guidance + body preview — below the header */}
                    <div className={`px-4 pb-4 ${isSubSection ? 'border-l-2 border-l-primary-300 dark:border-l-primary-700' : ''}`}>
                      {/* Guidance — editable prompt for AI generation */}
                      {section.sectionType !== 'static' && (
                        <div className="mt-1">
                          <label className="text-[10px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider">
                            Generation guidance
                          </label>
                          <textarea
                            value={section.guidance || ''}
                            onChange={(e) => {
                              setClassifiedSections(prev => prev?.map((s, i) =>
                                i === index ? { ...s, guidance: e.target.value || null } : s
                              ) ?? null);
                            }}
                            rows={4}
                            className="mt-1 w-full text-sm leading-relaxed border border-medical-200 dark:border-medical-600 rounded-lg px-3 py-2 bg-white dark:bg-medical-700 text-medical-700 dark:text-medical-300 resize-y focus:ring-1 focus:ring-primary-300 dark:focus:ring-primary-700 focus:border-primary-300 dark:focus:border-primary-700"
                            placeholder="Describe what content belongs in this section, how it should be structured, and what sub-topics to cover..."
                            disabled={isSubmitting}
                          />
                        </div>
                      )}

                      {/* Body preview — expandable */}
                      {section.bodyPreview && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setExpandedPreviews(prev => {
                              const next = new Set(prev);
                              if (next.has(index)) next.delete(index); else next.add(index);
                              return next;
                            })}
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-medical-400 dark:text-medical-500 hover:text-medical-600 dark:hover:text-medical-300 transition-colors"
                          >
                            <ChevronRight className={`h-3 w-3 transition-transform ${isPreviewExpanded ? 'rotate-90' : ''}`} />
                            <FileSearch className="h-3 w-3" />
                            Original content
                          </button>
                          {isPreviewExpanded && (
                            <div className="mt-1.5 p-3 rounded-lg bg-medical-50 dark:bg-medical-800/50 border border-medical-100 dark:border-medical-700">
                              <p className="text-xs text-medical-500 dark:text-medical-400 leading-relaxed whitespace-pre-wrap">
                                {section.bodyPreview}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary counts */}
            <div className="flex items-center gap-5 mt-5 pt-4 border-t border-medical-100 dark:border-medical-700">
              {(['static', 'variable', 'placeholder'] as const).map(type => {
                const count = classifiedSections.filter(s => s.sectionType === type).length;
                const config = SECTION_TYPE_CONFIG[type];
                return (
                  <span key={type} className={`inline-flex items-center gap-1.5 text-xs font-medium ${config.text}`}>
                    {config.icon}
                    {count} {config.label.toLowerCase()}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Manual sections — shown when NOT in DOCX mode */}
        {!isDocxMode && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">
                Sections <span className="text-sm font-normal text-medical-500">({sections.length})</span>
              </h2>
              <button
                type="button"
                onClick={addSection}
                disabled={isSubmitting}
                className="btn-secondary flex items-center gap-1.5 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Section
              </button>
            </div>

            <div className="space-y-4">
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  className="border border-medical-200 dark:border-medical-600 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    {/* Order number + reorder buttons */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <button
                        type="button"
                        onClick={() => moveSection(index, 'up')}
                        disabled={index === 0 || isSubmitting}
                        className="p-0.5 text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold text-xs">
                        {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => moveSection(index, 'down')}
                        disabled={index === sections.length - 1 || isSubmitting}
                        className="p-0.5 text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Section fields */}
                    <div className="flex-1 space-y-3">
                      <div>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSection(index, 'title', e.target.value)}
                          className="input-field w-full"
                          placeholder="Section title"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={section.placeholder || ''}
                          onChange={(e) => updateSection(index, 'placeholder', e.target.value)}
                          className="input-field w-full text-sm"
                          placeholder="Placeholder text (optional)"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        {/* Section type selector — shown when section has a sectionType (DOCX-based templates) */}
                        {section.sectionType && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-medical-500 dark:text-medical-400 text-xs">Type:</span>
                            <select
                              value={section.sectionType}
                              onChange={(e) => updateSection(index, 'sectionType', e.target.value)}
                              className="text-xs border border-medical-200 dark:border-medical-600 rounded-md px-2 py-1 bg-white dark:bg-medical-700 text-medical-700 dark:text-medical-300"
                              disabled={isSubmitting}
                            >
                              <option value="static">Static (keep as-is)</option>
                              <option value="variable">Variable (AI-generated)</option>
                              <option value="placeholder">Placeholder (manual)</option>
                            </select>
                          </div>
                        )}
                        <label className="flex items-center gap-2 text-sm text-medical-700 dark:text-medical-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={section.required}
                            onChange={(e) => updateSection(index, 'required', e.target.checked)}
                            className="rounded border-medical-300 text-primary-600 focus:ring-primary-500"
                            disabled={isSubmitting}
                          />
                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          Required
                        </label>
                        {!section.sectionType && (
                          <label className="flex items-center gap-2 text-sm text-medical-700 dark:text-medical-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={section.aiGenerated || false}
                              onChange={(e) => updateSection(index, 'aiGenerated', e.target.checked)}
                              className="rounded border-medical-300 text-primary-600 focus:ring-primary-500"
                              disabled={isSubmitting}
                            />
                            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                            AI-assisted
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => removeSection(index)}
                      disabled={sections.length <= 1 || isSubmitting}
                      className="p-1.5 text-medical-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove section"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/templates/${id}` : '/templates')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
            disabled={isSubmitting || isExtracting}
          >
            {isSubmitting ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                {isDocxMode ? 'Creating…' : 'Saving…'}
              </>
            ) : isExtracting ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Analysing document…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isDocxMode ? 'Create Template from Document' : isEdit ? 'Save Changes' : 'Create Template'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
